import { Component, Input, OnChanges, ChangeDetectionStrategy, Output, EventEmitter } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { ApiService } from 'app/services/api.service';
import { PermissionService } from 'app/services/permission.service';
import {
  ACCESS_LEVEL_RANK, AccessLevel, Areas, BUILT_IN_ROLE_NAME_KEYS, IAccessRole, IAccessRoleSettings, IArea, IStaffAccess,
  PermissionGrants
} from 'app/models/permission.model';
import {
  BuiltInRoleOption, RoleGrantContext, builtInGrantsOf, canGrantBuiltIn, canGrantCustom, isEmaacRole, isRoleAboveOwnAccess,
  parseRoleChoice, roleChoice, visibleBuiltInRoles
} from '../role-choice';

/** Override choice for one area: 'Role' keeps what the role gives */
type OverrideChoice = AccessLevel | 'Role';

/**
 * "Access details" on the staff card: the role in effect, every area's effective level and where it
 * comes from, and, when custom roles are enabled, individual permissions for this staff member only.
 * Users who may manage access rights change the role here too, without edit access to the staff member.
 */
@Component({
  selector: 'staff-access',
  templateUrl: './staff-access.component.html',
  styleUrls: ['./staff-access.component.css'],
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [NgClass, FormsModule, MatButtonToggleModule, MatFormFieldModule, MatSelectModule, MatTooltipModule, TranslateModule]
})
export class StaffAccessComponent implements OnChanges {
  @Input() staffId: string;
  /** Emitted after the staff member's access changed */
  @Output() accessChanged = new EventEmitter<IStaffAccess>();

  open = false;
  loading = false;
  loadError = false;
  areas: IArea[] = [];
  settings: IAccessRoleSettings;
  access: IStaffAccess | null = null;

  editing = false;
  overrides: Record<string, OverrideChoice> = {};
  saving = false;
  saveError: string | null = null;

  // Role change
  changingRole = false;
  rolesLoading = false;
  roleValue: string | null = null;
  customRoles: IAccessRole[] = [];
  private builtInGrants: Record<number, PermissionGrants> = {};
  private rolesLoaded = false;

  constructor(
    private api: ApiService,
    private permissions: PermissionService,
    private translate: TranslateService
  ) { }

  ngOnChanges(): void {
    this.open = false;
    this.editing = false;
    this.changingRole = false;
    this.access = null;
  }

  /** Shown only to users who may see access rights */
  get visible(): boolean {
    return !!this.staffId && this.permissions.has(Areas.StaffAccessRights, 'Read');
  }

  get canEditOverrides(): boolean {
    return !!this.settings?.customRolesEnabled && this.permissions.has(Areas.StaffAccessRights, 'ReadWrite') &&
      !this.hasEmaacRole;
  }

  /**
   * The role can be changed here by users who may manage access rights. EMAAC Operators and EMAAC
   * accounts keep a fixed role, which Everon admins change through the level in the staff form.
   */
  get canChangeRole(): boolean {
    return this.permissions.has(Areas.StaffAccessRights, 'ReadWrite') && !this.hasEmaacRole;
  }

  private get hasEmaacRole(): boolean {
    return this.access != null && !this.access.accessRoleId && isEmaacRole(this.access.level);
  }

  private get user(): { roleLevel: number } | null {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch {
      return null;
    }
  }

  private featureEnabled(feature: string): boolean {
    try {
      return (JSON.parse(localStorage.getItem('features')) ?? []).includes(feature);
    } catch {
      return false;
    }
  }

  private get grantContext(): RoleGrantContext {
    return { userLevel: this.user?.roleLevel, permissions: this.permissions, builtInGrants: this.builtInGrants };
  }

  private get currentRole() {
    return { accessRoleId: this.access?.accessRoleId ?? null, level: this.access?.level };
  }

  get builtInOptions(): BuiltInRoleOption[] {
    return visibleBuiltInRoles(f => this.featureEnabled(f), this.currentRole, this.user?.roleLevel);
  }

  /** Custom roles are offered when enabled, and the current custom role is always listed */
  get customOptions(): IAccessRole[] {
    return this.settings?.customRolesEnabled || this.access?.accessRoleId ? this.customRoles : [];
  }

  canGrantBuiltIn(level: number): boolean {
    return canGrantBuiltIn(this.grantContext, level);
  }

  canGrantCustom(role: IAccessRole): boolean {
    return canGrantCustom(this.grantContext, role);
  }

  /** The staff member has more access than the user: the API refuses any change */
  get roleAboveOwnAccess(): boolean {
    return !!this.access && this.rolesLoaded && isRoleAboveOwnAccess(this.grantContext, this.currentRole, this.customRoles);
  }

  startRoleChange(): void {
    this.saveError = null;
    this.roleValue = roleChoice(this.access.accessRoleId ?? null, this.access.level);
    this.changingRole = true;
    if (this.rolesLoaded) return;

    this.rolesLoading = true;
    this.api.getAccessRoles().subscribe({
      next: roles => {
        this.customRoles = (roles ?? []).filter(r => !r.builtIn);
        this.builtInGrants = builtInGrantsOf(roles ?? []);
        this.rolesLoaded = true;
        this.rolesLoading = false;
      },
      error: () => {
        this.rolesLoading = false;
        this.changingRole = false;
        this.saveError = this.translate.instant('ROLES_LOAD_FAILED');
      }
    });
  }

  saveRole(): void {
    const assignment = parseRoleChoice(this.roleValue);
    if (!assignment || this.roleValue === roleChoice(this.access.accessRoleId ?? null, this.access.level)) {
      this.changingRole = false;
      return;
    }

    this.saving = true;
    this.saveError = null;
    this.api.assignStaffAccessRole(this.staffId, assignment).subscribe({
      next: access => {
        this.access = access;
        this.saving = false;
        this.changingRole = false;
        this.accessChanged.emit(access);
      },
      error: err => {
        this.saving = false;
        this.saveError = this.errorMessage(err);
      }
    });
  }

  private errorMessage(err: any): string {
    const detail = typeof err?.error === 'string' ? ' ' + err.error : '';
    return this.translate.instant(err?.status === 403 ? 'ROLES_ERROR_ABOVE_OWN_ACCESS' : 'ROLES_ERROR_CONFLICT') + detail;
  }

  get overrideCount(): number {
    return Object.keys(this.access?.overrides ?? {}).length;
  }

  toggle(): void {
    this.open = !this.open;
    if (this.open && !this.access) this.load();
  }

  load(): void {
    this.loading = true;
    forkJoin({
      areas: this.api.getPermissionAreas(),
      settings: this.api.getAccessRoleSettings(),
      access: this.api.getStaffAccess(this.staffId)
    }).subscribe({
      next: ({ areas, settings, access }) => {
        this.areas = areas;
        this.settings = settings;
        this.access = access;
        this.loading = false;
        this.loadError = false;
      },
      error: () => {
        this.loading = false;
        this.loadError = true;
      }
    });
  }

  /** Role name: custom roles by name, built-in roles by their translated level name */
  get roleName(): string {
    if (!this.access) return '';
    if (this.access.accessRoleId) return this.access.roleName;
    const key = BUILT_IN_ROLE_NAME_KEYS[this.access.level];
    return key ? this.translate.instant(key) : this.access.roleName;
  }

  depth(area: IArea): number {
    return area.key.split('.').length - 1;
  }

  levelLabel(level: AccessLevel): string {
    return this.translate.instant({ None: 'ACCESS_NONE', Read: 'ACCESS_READ', ReadWrite: 'ACCESS_READWRITE' }[level] ?? level);
  }

  sourceLabel(area: IArea): string {
    const grant = this.access?.effective[area.key];
    switch (grant?.source) {
      case 'Override': return this.translate.instant('STAFF_ACCESS_SOURCE_INDIVIDUAL');
      case 'Role': return this.translate.instant('STAFF_ACCESS_SOURCE_ROLE');
      case 'BuiltIn': return this.translate.instant('STAFF_ACCESS_SOURCE_ROLE');
      case 'Tier': return this.translate.instant('STAFF_ACCESS_SOURCE_ACCOUNT_TYPE');
      default: return '';
    }
  }

  startEditing(): void {
    this.overrides = {};
    for (const area of this.areas) {
      this.overrides[area.key] = this.access.overrides[area.key] ?? 'Role';
    }
    this.saveError = null;
    this.editing = true;
  }

  setOverride(key: string, choice: OverrideChoice): void {
    this.overrides = { ...this.overrides, [key]: choice };
  }

  /** Levels above the user's own access on the area can't be given */
  aboveOwnAccess(key: string, level: AccessLevel): boolean {
    return ACCESS_LEVEL_RANK[level] > ACCESS_LEVEL_RANK[this.permissions.resolve(key)];
  }

  saveOverrides(): void {
    const grants: PermissionGrants = {};
    for (const [key, choice] of Object.entries(this.overrides)) {
      if (choice !== 'Role') grants[key] = choice;
    }

    this.saving = true;
    this.saveError = null;
    this.api.saveStaffAccessOverrides(this.staffId, grants).subscribe({
      next: access => {
        this.access = access;
        this.saving = false;
        this.editing = false;
        this.accessChanged.emit(access);
      },
      error: err => {
        this.saving = false;
        this.saveError = this.errorMessage(err);
      }
    });
  }
}
